/**
 * useBookmarkCheck Hook
 * Hook pour vérifier si un item est bookmarké
 */

"use client"

import { useEffect, useState } from "react"
import { checkBookmark } from "../api/bookmarks-api"
import type {
    Bookmark,
    UseBookmarkCheckResult,
} from "../types/bookmark.types"
import { isInCache } from "../utils/bookmark-storage"

interface UseBookmarkCheckOptions {
  entityType: string
  entityId: string
  userEmail?: string
  enabled?: boolean
}

export function useBookmarkCheck(
  options: UseBookmarkCheckOptions
): UseBookmarkCheckResult {
  const { entityType, entityId, userEmail, enabled = true } = options

  const [isBookmarked, setIsBookmarked] = useState<boolean>(false)
  const [bookmark, setBookmark] = useState<Bookmark | null>(null)
  const [isChecking, setIsChecking] = useState<boolean>(true)

  useEffect(() => {
    if (!enabled || !entityType || !entityId) {
      setIsChecking(false)
      return
    }

    async function checkStatus() {
      try {
        setIsChecking(true)

        // Vérifier d'abord dans le cache pour une réponse immédiate
        if (userEmail) {
          const inCache = isInCache(userEmail, entityType, entityId)
          if (inCache) {
            setIsBookmarked(true)
            // Continue en arrière-plan pour confirmer avec le serveur
          }
        }

        // Vérifier avec le serveur
        const response = await checkBookmark({ entityType, entityId })
        setIsBookmarked(response.bookmarked)
        setBookmark(response.bookmark || null)
      } catch (error) {
        console.error("Error checking bookmark status:", error)
        // En cas d'erreur, garder l'état du cache
      } finally {
        setIsChecking(false)
      }
    }

    checkStatus()
  }, [entityType, entityId, userEmail, enabled])

  return {
    isBookmarked,
    bookmark,
    isChecking,
  }
}
